import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { VideoEnvironmentProps } from '../../cvp-webgames-core/Components/Environments/React/VideoEnvironment';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import BasePracticeScenario from '../base-practice-scenario/BasePracticeScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { SuccessType } from '../general/general-scenario-stuff';
import { Windows } from '../../misc/tools';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import BaseTestScenario from '../base-test-scenario/BaseTestScenario';
import { getTokenSourceMapRange } from 'typescript';
import { FloatingMessagesUIConfig } from '../../components/ui/FloatingMessagesUI';

export default class WilliamHoward extends BaseTestScenario {		
	public get maxScore(): number {
		return 52800;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 0; //patient has dentures
		var setupFiberOpticsPoints = 1000;
		var prePoints = 1000;
		var correctAttempt = 32700;


		return assessPatientRemoveDenturesPoints + 
				setupFiberOpticsPoints +
				prePoints +
				correctAttempt;
    }

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 12;
    }
	
	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 130,
			bloodPressureDenominator: 80,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 94,
			heartRate: 80,
			heartRateMax: 150,
			respirationRate: 25,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
            onInductionTimerRanOut: () => {
                context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for William Howard. He has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}
	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);
		
		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				'Due to the patient\'s severe edema, you will be unable to place a bronchoscope in the patient\s oropharynx.',
				'With a protruding, edematous tongue, bag valve mask will be nearly impossible. It will also make it impossible to place a supraglottic airway or oral airway.',
				'The Vortex approach recommends you make an optimal attempt at each technique; however, because of Mr. Howard\'s large protruding tongue, you can make the clinical judgement to move to a cricothyrotomy before completing optimization of all three techniques. You should attempt to visualize the airway with flexible bronchoscope before moving to the cricothyrotomy.',
				'Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?'
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished() : void  {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished() : void  {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onSGAFinished() : void  {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onCricFinished() : void  {
		this.addScore(this.predeterminedPoints);
		this._CheckOxy();
		this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.Cric,'You successfully performed a cricothyrotomy and established a stable, patent airway for William Howard after he became apneic.  Mr. Howard was apneic for ' + this.apneaTimeElapsed + ' while you managed his airway. A team arrives shortly thereafter to establish a definitive airway for Mr. Howard. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief for a summary of key learning points.','Good Job on this Scenario'); 
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}
	}

	public getAssessAirwayData() : AssessAirwayActionData[]  {
		const context = this;
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'Mobile larynx and trachea; midline is not deviated. Thyroid and cricoid cartilage are palpable.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Thyromental Distance: 3 finger breadths (4 cm)<br />Hyoid to Thyroid Notch Distance: (2 cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'You see the patient moving his chin to his chest.'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The dentures have been removed',
				onClick: () => {					
					context.performActionWithWarning('Remove Dentures', 'The patient\'s dentures will be removed.');
				}
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'Dentures'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				imgUrl: './assets/images/assess-airway-images/Enlargedtongue.png',
				title: 'Open mouth',
				text: 'Inter-incisor Distance: cannot assess<br />Mallampati: cannot assess'
			}
		]
    }

	private _setupIntro () {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			This is William Howard. He’s a 54-year-old male, 70 in, 100 kg. 
		// 			His potassium is 4.2, and he ate breakfast this morning. He has CAD, 
		// 			hypertension, and hyperlipidemia. His PMD started lisinopril last week. 
		// 			He was admitted for dyspnea due to facial edema involving the tongue.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario11 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 				He was admitted for observation, and our working diagnosis is angioedema. 
		// 				Within the last 30 minutes his angioedema has progressed.
		// 				I’m already administering epinephrine and steroids. I I’ll manage the
		// 				hemodynamics; you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario11 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `He was admitted for dyspnea due to facial edema involving the tongue.`,
		// 		b: `
		// 			Age: 54<br />
		// 			Height (in): 70 in<br />
		// 			Weight (kg): 100 kg<br />
		// 			Potassium (K): 4.2<br />
		// 			NPO Status: He ate 2 hours ago.<br />
		// 			<br />
		// 			PMH: He has CAD, hypertension and hyperlipidemia. His PMD added lisinopril last week.
		// 		`,
		// 		a: `He was admitted for observation, and out working diagnosis is angioedema. This patient has dyspnea due to facial edema involving the tongue. Within the last 30 minutes his angioedema has progressed.`,
		// 		r: `His physician is asking for intubation for airway protection.`
		// 	}
		// }

		this._id_ScenarioSet(11); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario11 - intro - 53'
				}
			],
			sbarConfig: {
				s: `He was admitted for dyspnea due to facial edema involving the tongue.`,
				b: `
					Age: 54<br />
					Height (in): 70 in<br />
					Weight (kg): 100 kg<br />
					Potassium (K): 4.2<br />
					NPO Status: He ate 2 hours ago.<br />
					<br />
					PMH: He has CAD, hypertension and hyperlipidemia. His PMD added lisinopril last week.
				`,
				b508: `
					Age. 54.
					Height. 70 inches.
					Weight. 100 kilograms.
					Potassium. 4.2.
					NPO Status. He ate 2 hours ago.
					Past Medical History. He has CAD hypertension and hyperlipidemia. His PMD added lisinopril last week.
				`,
				a: `He was admitted for observation, and out working diagnosis is angioedema. This patient has dyspnea due to facial edema involving the tongue. Within the last 30 minutes his angioedema has progressed.`,
				r: `His physician is asking for intubation for airway protection.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario11 - Flat - Nothing - 1',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader');
			},
			isCric: true
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			toolsToChooseFrom: toolsArr
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario11 - Flat - Nothing - 1',
			onSuctionSet: () => {
				this.debriefLog("Suction set");
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario11 - 45 - Nothing - 7',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario11 - 45 - patient-assessment-up - 36',
			chinToChestVideoChapter: () =>'DAARC - Scenario11 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario11 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario11 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario11 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart")
			}		
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // this needs to update based on the selection made		
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection); // this should update the pre-oxygenation neutral video segment, but the reference set above might not reflect this update
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE,
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupCric();
		this._setupAuscultate();
	}

	public getDefaultSa02() {
		return 94
	}

	public getDefaultApneaTime() {
		return 210;
	}

	private _setupBvm() {
		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount
			}
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.part1Config = {
			hideVlButton: () => true,
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			ettSpecialUIConfig: {
				airwayImage: () => {
					return './assets/images/ett/Grade4Edema.png';
				},
				magillForceps: {
					text: () => 'There is no foreign body in the patient’s airway.'
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => ''
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount
			}
		};
	}

	private _setupSga() {
		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			squeezeAmbuBagVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			insertingSgaVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount
			}
		};
	}

	private _setupCric() {
		this.procedures.cric.config.isAttemptSuccessful = () => {
			return true;
		}

		this.procedures.cric.config.isCricScenario = true;

		this.procedures.cric.config.part1Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			correctToolOrder: [
				'Scalpel',
				'Spreader',
				'Bougie',
				'ETT',
				'Syringe',
			]
		};
		this.procedures.cric.config.part2Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			identifyLandmarksVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(1), 'flat'),
		};
		this.procedures.cric.config.part3Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4'
		};
		this.procedures.cric.config.part4Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			scalpelVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(2), 'flat'),
			onMakingIncition: () => {
				this.cricAttemptCount++;
			}	
		};
		this.procedures.cric.config.part5Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			spreaderVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(3), 'flat')
		};
		this.procedures.cric.config.part6Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			bougieVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(4), 'flat')
		};
		this.procedures.cric.config.part7Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			etTubeVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(5), 'flat')
		};
		this.procedures.cric.config.part8Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(6), 'Flat')
		};

		this.procedures.cric.config.part9Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			onYesClicked: () => {
				this.addScore(1200) // lite cric points that are normally awarded during cric
			},
			onNoClicked: () => {
				this.showFailedScenarioPopUp('You absolutely need to have the level 3 OORAM provider perform the cric.','Failed Scenario')
			}
		};
	}

	private _setupAuscultate() {
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario11/daarc-scenario11-williamhoward-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),
                checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathNo;
				}
            },
            ettConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),
                checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathNo;
				}
            },
            sgaConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),
                checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathNo;
				}
            },
            cricConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),
                checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathYes;
				}
			}
		};
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}
}